//  React & 3rd Party Libraries
import { useEffect, useMemo, useRef, useState, FC } from "react";
import { Button } from "@mui/material";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useDebounce, useDebouncedCallback } from "use-debounce";

// Icons
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import ZoomInMapOutlinedIcon from "@mui/icons-material/ZoomInMapOutlined";
import ReorderOutlinedIcon from "@mui/icons-material/ReorderOutlined";

// APIs, custom hooks, utilities
import ContactsAPI from "@/api/contacts";
import { usePermissions } from "@/hooks/usePermissions";
import { getDateFormat } from "@/utils/date-format";
import { useQueryFilter } from "@/Monolith-UI/QueryFilter/QueryFilter.js";
import synchronizeColumnState from "@/utils/synchronize-column-state.js";

// Custom components
import Loader from "@/components/Loader.js";
import Table, { Column, useTable } from "@/Monolith-UI/Table/Table.js";
import ComboButton from "@/Monolith-UI/ComboButton/ComboButton.js";
import ContactsColumnDefs from "@/components/Contacts/ContactsColumnDefs.js";
import { addCustomFieldsToColumnDefs } from "@/components/Evidence/CustomFieldCell.js";
import { ItemTotalBadge } from "@/Features";
import { useTheme, Input } from "@monolith-forensics/monolith-ui";
import CustomFieldsApi from "@/api/custom-fields";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON.js";
import { ContactFlyout } from "../Flyouts/ContactFlyout";
import { CreateContactModal } from "../Modals/CreateContactModal";
import { Contact } from "../contacts.types";
import { MONOLITH_PERMISSIONS } from "@/constants";
import { CustomField, CUSTOM_FIELD_CATEGORIES } from "@/Features";
import { FilterDimension as BaseFilterDimension } from "@/Features/Acquisitions/acquisitions.types";
import { TableColumnDef } from "@/Monolith-UI/Table/types/Table";

interface ContactsTableProps {
  stateStoreKey?: string;
}

// Extend the base FilterDimension to add options property needed for custom fields
interface FilterDimension extends BaseFilterDimension {
  options?: { option: string }[];
  addtionalSelections?: any[];
}

interface QueryCondition {
  id: string;
  field: string;
  operator: string;
  value: any;
  queryComplete: boolean;
  selections: any[];
}

interface ContactsQuery {
  conditions?: QueryCondition[];
  page?: number;
  pageSize?: number;
  search?: string | null;
  order?: {
    field: string;
    sort: string;
  } | null;
}

// Extend the generic TableColumnDef for Contact-specific columns
type ContactColumnState = TableColumnDef<Contact> & {
  showInColumnChooser?: boolean;
};

interface ColumnReorderItem {
  column: string;
  order: number;
}

interface ColumnResizeEvent {
  columns: Array<{
    dataField: string;
    width: string;
  }>;
}

const visibleColumns = [
  "name",
  "email",
  "title",
  "organization",
  "contact_type",
];

const ContactsTable: FC<ContactsTableProps> = ({
  stateStoreKey = "contacts:view",
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const queryFilter = useRef(null);

  const pageSize = 100;

  const [isFlyoutVisible, setIsFlyoutVisible] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [flyoutData, setFlyoutData] = useState<Contact | null>(null);

  const table = useTable();

  const { hasPermission } = usePermissions();

  const [columnState, setColumnState] = useState<ContactColumnState[]>(() => {
    return synchronizeColumnState(
      ContactsColumnDefs.map((col) => {
        return {
          ...col,
          visible: visibleColumns.includes(col.dataField),
        };
      }),
      JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
    );
  });

  const [searchText, setSearchText] = useState<string | null>(
    localStorage.getItem(`${stateStoreKey}:searchText`)
  );
  const [debouncedSearchText] = useDebounce(searchText, 500);

  const currentSort = useMemo(() => {
    let [sort] = columnState
      ?.filter((c) => c.sorting?.active)
      .map((c) => ({ field: c.dataField, sort: c.sorting?.direction }));

    return sort;
  }, [columnState]);

  const { data: customFields } = useQuery<CustomField[]>({
    queryKey: ["contacts:customFields:list"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.PEOPLE,
      }),
  });

  // Create contact dimensions for filtering
  const contactDimensions = useMemo(() => {
    const baseDimensions = [
      {
        name: "Name",
        field: "name",
        id: "name",
        type: "text",
        mode: "text",
      },
      {
        name: "Email",
        field: "email",
        id: "email",
        type: "text",
        mode: "text",
      },
      {
        name: "Title",
        field: "title",
        id: "title",
        type: "text",
        mode: "text",
      },
      {
        name: "Organization",
        field: "organization",
        id: "organization",
        type: "string",
        mode: "multi-select",
        key: ["contacts:organizations"],
        selectionDisplayName: "organization",
        selectionIdField: "organization",
      },
      {
        name: "Contact Type",
        field: "contact_type",
        id: "contact_type",
        type: "string",
        mode: "multi-select",
        key: ["contacts:types"],
        selectionDisplayName: "contact_type",
        selectionIdField: "contact_type",
      },
      {
        name: "Address",
        field: "address",
        id: "address",
        type: "text",
        mode: "text",
      },
      {
        name: "City",
        field: "city",
        id: "city",
        type: "text",
        mode: "text",
      },
      {
        name: "State",
        field: "state",
        id: "state",
        type: "text",
        mode: "text",
      },
      {
        name: "Zipcode",
        field: "zipcode",
        id: "zipcode",
        type: "text",
        mode: "text",
      },
      {
        name: "Mobile",
        field: "mobile_number",
        id: "mobile_number",
        type: "text",
        mode: "text",
      },
      {
        name: "Phone",
        field: "office_number",
        id: "office_number",
        type: "text",
        mode: "text",
      },
      {
        name: "Notes",
        field: "notes",
        id: "notes",
        type: "text",
        mode: "text",
      },
      {
        name: "Unique ID",
        field: "unique_id",
        id: "unique_id",
        type: "text",
        mode: "text",
      },
    ];

    if (customFields) {
      return [
        ...baseDimensions,
        ...customFields.map(
          (field: CustomField): FilterDimension => ({
            name: field.field_name,
            field: `custom_field_${field.field_id}`,
            id: `custom_field_${field.field_id}`,
            type:
              field.editor_type === "dateBox"
                ? "date"
                : field.editor_type === "dropDown" ||
                  field.editor_type === "tagBox"
                ? "string"
                : "text",
            mode:
              field.editor_type === "dateBox"
                ? "date"
                : field.editor_type === "dropDown" ||
                  field.editor_type === "tagBox"
                ? "multi-select"
                : "text",
            options: field.options
              ? (typeof field.options === "string"
                  ? safeParseJSON(field.options)
                  : field.options
                ).map((o: string) => ({ option: o }))
              : undefined,
            selectionDisplayName: field.options ? "option" : undefined,
            selectionIdField: field.options ? "option" : undefined,
          })
        ),
      ];
    }
    return baseDimensions;
  }, [customFields]);

  const handleFilter = (newFilter: Partial<ContactsQuery>) => {
    setQuery((q) => {
      return {
        ...q,
        ...newFilter,
      };
    });
  };

  const { queryButton, conditions, resolvedQuery } = useQueryFilter({
    dimensions: contactDimensions.sort((a, b) => a.name.localeCompare(b.name)),
    onQuerySet: handleFilter,
    queryFilter: queryFilter,
    stateStoring: {
      enabled: true,
      type: "localStorage",
      storageKey: stateStoreKey,
    },
  });

  const [query, setQuery] = useState<ContactsQuery>({
    conditions: resolvedQuery?.conditions || [],
  });

  const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        stateStoreKey,
        {
          query: {
            ...query,
            order: query?.order || currentSort,
            pageSize,
            search: debouncedSearchText ? debouncedSearchText : null,
          },
        },
      ],
      queryFn: ({ pageParam }) =>
        ContactsAPI.query({
          query: {
            ...query,
            order: query?.order || currentSort,
            pageSize,
            search: debouncedSearchText ? debouncedSearchText : null,
            page: pageParam,
          },
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.page - 1 === 0) return null;
        return firstPage.page - 1;
      },
      initialPageParam: 1,
      enabled: !!query,
      placeholderData: (data) => data,
    });

  const records =
    data?.pages?.reduce((acc, page) => {
      return [...acc, ...(page?.data || [])];
    }, []) || [];

  const totalRecords = data?.pages?.[0]?.total || 0;

  const debouncedFetchNextPage = useDebouncedCallback((e) => {
    fetchNextPage();
  }, 50);

  const handleContactsRefetch = () => {
    refetch();
  };

  const handleColumnVisibility = (
    column: ContactColumnState,
    visible: boolean
  ) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        if (c.dataField === column.dataField) {
          return {
            ...c,
            visible,
          };
        }
        return c;
      });
    });
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleReload = () => {
    refetch();
  };

  const handleClearFilters = () => {
    (queryFilter.current as any)?.clear();
  };

  const handleSort = (field: string) => {
    const savedColumn = columnState?.find((svc) => field === svc.dataField);
    const order = savedColumn?.sorting?.direction || null;

    let newOrder = null;

    // if sorted on a different column, reset the sort
    if (query?.order?.field !== field) {
      newOrder = {
        field,
        sort: "asc",
      };
    }

    // otherwise rotate the sort options on the current column
    else {
      switch (order) {
        case "asc":
          newOrder = {
            field,
            sort: "desc",
          };
          break;
        case "desc":
          newOrder = null;
          break;
        default:
          newOrder = {
            field,
            sort: "asc",
          };
      }
    }

    let newCols = columnState?.map((c) => {
      if (c.dataField === field) {
        return {
          ...c,
          sorting: {
            active: newOrder ? true : false,
            direction: newOrder?.sort as "asc" | "desc" | undefined,
          },
        };
      }
      // Create a new object without sorting instead of mutating the original
      const { sorting, ...colWithoutSorting } = c;
      return colWithoutSorting as ContactColumnState;
    });

    let newQuery = {
      ...query,
      order: newOrder,
    };

    setColumnState(newCols);
    setQuery(newQuery);
  };

  const handleColumnReorder = (newOrder: ColumnReorderItem[]) => {
    setColumnState((cs) => {
      // Create a map of existing columns for faster lookup
      const existingColumns = new Map(cs.map((col) => [col.dataField, col]));

      // Only reorder columns that actually exist in current state
      const reorderedColumns = newOrder
        .filter((o: ColumnReorderItem) => existingColumns.has(o.column))
        .map((o: ColumnReorderItem) => {
          const existingColumn = existingColumns.get(o.column)!;
          return {
            ...existingColumn,
            order: o.order,
          };
        });

      // Add any columns that weren't in the reorder list (like newly added custom fields)
      const reorderedDataFields = new Set(
        reorderedColumns.map((col: ContactColumnState) => col.dataField)
      );
      const missingColumns = cs.filter(
        (col) => !reorderedDataFields.has(col.dataField)
      );

      return [...reorderedColumns, ...missingColumns];
    });
  };

  const handleColumnResize = (e: ColumnResizeEvent) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        const col = e.columns.find((col) => col.dataField === c.dataField);
        if (col) {
          return {
            ...c,
            width: parseInt(col.width.replace(/px/g, "")),
          };
        }
        return c;
      });
    });
  };

  const handleActionButtonClick = (rowData: Contact) => {
    setIsFlyoutVisible(true);
    setFlyoutData(rowData);
  };

  const handleGetNextItem = (client_id: number) => {
    const current = records?.findIndex(
      (i: Contact) => i.client_id === client_id
    );
    const nextItem = records[current + 1] || records[0];
    setFlyoutData(nextItem);
  };

  const handleGetPrevItem = (client_id: number) => {
    const current = records?.findIndex(
      (i: Contact) => i.client_id === client_id
    );
    const prevItem = records[current - 1] || records[records.length - 1];
    setFlyoutData(prevItem);
  };

  // Detect scroll to bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <=
      clientHeight + 100 * (data?.pages?.length || 0)
    ) {
      if (hasNextPage && !isFetchingNextPage) {
        debouncedFetchNextPage(e);
      }
    }
  };

  const handleExportContactsTable = () => {
    // show snackbar
    enqueueSnackbar("Exporting table...", {
      variant: "info",
    });

    ContactsAPI.exportContactsList({
      query: {
        ...query,
        order: query?.order || currentSort,
      },
      type: "xlsx",
      columns: columnState
        .filter((c) => c.visible !== false)
        .sort((a, b) => a.order - b.order)
        .map((c) => {
          const { caption, ...rest } = c;
          return { ...rest, header: caption };
        }),
      date_format: getDateFormat({ isMoment: true, includeTime: true }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );

      el.download = filename;
      el.click();
    });
  };

  useQueries({
    queries: [
      {
        queryKey: ["contacts:organizations"],
        queryFn: () => ContactsAPI.getOrganizations(),
        enabled: !!data,
      },
      {
        queryKey: ["contacts:types"],
        queryFn: () => ContactsAPI.getContactTypes(),
        enabled: !!data,
      },
      {
        // Fetch custom fields
        queryKey: ["contacts:customFields:list"],
        queryFn: () =>
          CustomFieldsApi.getCustomFields({
            category: CUSTOM_FIELD_CATEGORIES.PEOPLE,
          }),
        enabled: !!data,
      },
    ],
  });

  // Sync column state to local storage
  useEffect(() => {
    if (!stateStoreKey) return;
    let oldState = JSON.parse(localStorage.getItem(stateStoreKey) || "{}");
    localStorage.setItem(
      stateStoreKey,
      JSON.stringify({
        ...oldState,
        cols: columnState,
      })
    );
  }, [columnState, stateStoreKey]);

  // Add custom fields to column state
  useEffect(() => {
    if (!customFields) return;
    setColumnState((cs) => {
      return addCustomFieldsToColumnDefs(
        customFields,
        cs
      ) as ContactColumnState[];
    });
  }, [customFields]);

  useEffect(() => {
    setQuery((q) => ({
      ...q,
      search: debouncedSearchText ? debouncedSearchText : null,
      page: 1,
    }));
  }, [debouncedSearchText]);

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
      }}
    >
      <div
        style={{
          display: "flex",
          flex: "initial",
          flexDirection: "row",
          alignContent: "center",
          alignItems: "center",
          marginBottom: 10,
        }}
      >
        {hasPermission(MONOLITH_PERMISSIONS.CONTACTS_CREATE) && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            // Deliberate twin with CaseContactsView.js: the testid names the
            // "new contact" contract, not the component, so specs survive the
            // legacy/new swap. Don't rename one side (Testing Philosophy doc).
            data-testid="contacts-new-button"
            onClick={() => setShowCreateModal(true)}
            style={{
              minWidth: "fit-content",
              fontSize: 11,
              padding: "3px 6px",
            }}
          >
            + New Contact
          </Button>
        )}
        <div
          style={{
            marginLeft: hasPermission(MONOLITH_PERMISSIONS.CONTACTS_CREATE)
              ? 10
              : 0,
          }}
        >
          {queryButton}
        </div>
        <ItemTotalBadge total={totalRecords || 0} />
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
            minWidth: "fit-content",
          }}
        >
          <ComboButton
            type="multi-select"
            data={columnState.filter((c) => c.showInColumnChooser !== false)}
            displayField="caption"
            idField={"dataField"}
            selectedItems={columnState.filter((c) => c.visible !== false)}
            variant="outlined"
            closeOnSelect={false}
            showSearch={true}
            dropDownTitle={() => {
              return (
                <div
                  style={{
                    margin: "5px 0px",
                    padding: 3,
                    color: theme.palette.text.secondary,
                    display: "flex",
                    alignItems: "center",
                    minWidth: 200,
                  }}
                >
                  Select Columns
                </div>
              );
            }}
            onItemDeSelect={(item) => {
              handleColumnVisibility(item, false);
            }}
            onItemSelect={(item) => {
              handleColumnVisibility(item, true);
            }}
            textColor={theme.palette.text.secondary}
            title={"Select Columns"}
          >
            <ViewColumnOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Export Table"}
            onClick={handleExportContactsTable}
            data={[]}
            selectedItems={[]}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Clear Filters"}
            onClick={handleClearFilters}
            data={[]}
            selectedItems={[]}
          >
            <FilterListOffIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={table.isCompact ? "Zoom In" : "Zoom Out"}
            onClick={() => table.toggleCompact()}
            data={[]}
            selectedItems={[]}
          >
            {table.isCompact && (
              <ZoomOutMapOutlinedIcon style={{ fontSize: 18 }} />
            )}
            {!table.isCompact && (
              <ZoomInMapOutlinedIcon style={{ fontSize: 18 }} />
            )}
          </ComboButton>
          <ComboButton
            type="button"
            variant={"outlined"}
            textColor={
              table.isStriped
                ? theme.palette.primary.main
                : theme.palette.text.secondary
            }
            title={table.isStriped ? "Hide Stripes" : "Show Stripes"}
            onClick={() => table.toggleStripes()}
            data={[]}
            selectedItems={[]}
          >
            <ReorderOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Refresh Table"}
            onClick={handleRefresh}
            data={[]}
            selectedItems={[]}
          >
            <SyncOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          {/* SEARCH */}
          <Input
            placeholder="Search Contacts"
            variant="outlined"
            height={30}
            defaultValue={searchText}
            onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const currentValue = (e.target as HTMLInputElement).value.trim();
              setSearchText(currentValue);

              localStorage.setItem(`${stateStoreKey}:searchText`, currentValue);
            }}
            style={{
              marginLeft: "10px",
              border: searchText
                ? `1px solid ${theme.palette.primary.main}`
                : "",
            }}
          />
        </div>
      </div>
      {data && (
        <>
          {<div>{conditions}</div>}
          <Table
            data={records || []}
            totalRecords={totalRecords || 0}
            reloadFn={handleReload}
            keyValue="client_id"
            tableInstance={table}
            onHeaderClick={(col: ContactColumnState) =>
              col?.sorting?.enabled === false ? null : handleSort(col.dataField)
            }
            onScroll={handleScroll}
            onColumnReorder={handleColumnReorder}
            onColumnResize={handleColumnResize}
            showActionColumn={true}
            onActionButtonClick={handleActionButtonClick}
            focusedRow={flyoutData}
          >
            {columnState.map((col) => {
              return <Column key={col.dataField} {...col} />;
            })}
          </Table>
        </>
      )}
      {!data && <Loader message={"Retrieving Contacts..."} />}
      {isFlyoutVisible && flyoutData && (
        <ContactFlyout
          show={isFlyoutVisible}
          onClose={() => {
            setIsFlyoutVisible(false);
            setFlyoutData(null);
          }}
          onDelete={() => {
            setIsFlyoutVisible(false);
            setFlyoutData(null);
            refetch();
          }}
          onEdit={handleContactsRefetch}
          defaultInfo={flyoutData}
          onNext={() => handleGetNextItem(flyoutData.client_id)}
          onPrevious={() => handleGetPrevItem(flyoutData.client_id)}
        />
      )}
      <CreateContactModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCancel={() => setShowCreateModal(false)}
        onSubmit={handleContactsRefetch}
      />
    </div>
  );
};

export default ContactsTable;
